'use client';

import { useCallback, useEffect, MouseEvent, useRef, useContext } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { getContextMenuPositionByTarget } from '@nx-frontend/shared/ui/context-menu';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { $ModalStore, openModalEvent, closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { createPortal } from '@nx-frontend/shared/actions';
import { useBuyPortalAddon } from '@nx-frontend/shared/hooks';
import { useRouter, useSearchParams } from '@nx-frontend/shared/hooks/router-proxy';
import { $isFreePlan } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { setNoteFullScreenEvent } from '@nx-frontend/shared/store/note-fullscreen';
import { $isNoteFullScreen } from '@nx-frontend/shared/store/note-fullscreen';
import { $notes } from '@nx-frontend/shared/store/notes';
import { $orgPortals } from '@nx-frontend/shared/store/org-portals';
import { $opCanCreatePortal, $opCanManageBilling } from '@nx-frontend/shared/store/org-role';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $portalsUsage } from '@nx-frontend/shared/store/portal-usage';
import { $isBusinessTrialActive } from '@nx-frontend/shared/store/trial';
import { $webEditor } from '@nx-frontend/shared/store/web-editor-store';
import { PopupType } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps } from '@nx-frontend/shared/types';
import { NotesStoreNote } from '@nx-frontend/shared/types';
import { NextRouterType } from '@nx-frontend/shared/utils';
import { TopbarContext } from '@nx-frontend/widgets/topbar';
import { PageMenu } from './page-menu';

interface NoteTopbarProps {
  globalId: string;
  isCurrentNoteFetching: boolean;
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
}

export function NoteTopbar(props: NoteTopbarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const { noteMenuItem: noteInMenu } = useContext(TopbarContext) || {};

  const [
    notes,
    currentOrganization,
    currentWorkspace,
    orgPortals,
    portalsUsage,
    isFreePlan,
    isBusinessTrialActive,
    opCanCreatePortal,
    opCanManageBilling,
    modalStore,
    webEditor,
    isNoteFullScreen,
    portalSettings,
  ] = useUnit([
    $notes,
    $currentOrganization,
    $currentWorkspace,
    $orgPortals,
    $portalsUsage,
    $isFreePlan,
    $isBusinessTrialActive,
    $opCanCreatePortal,
    $opCanManageBilling,
    $ModalStore,
    $webEditor,
    $isNoteFullScreen,
    $portalSettings,
  ]);

  const [openModal, closeModal] = useUnit([openModalEvent, closeModalEvent]);

  const t = useTranslations('webnotes');

  const { getOpenPaywallPopup } = props;
  const workspaceId = currentWorkspace.globalId;
  const noteId = props.globalId;
  const entities = notes.workspaces[workspaceId]?.entities || {};
  const note: NotesStoreNote | undefined = entities[noteId];

  const openPortalAddonPupup = useBuyPortalAddon();

  const portalPageUrl = searchParams?.get('portal-page-url');

  const handleAddedToPortalClick = useCallback(
    (event: MouseEvent) => {
      if (!portalButtonRef.current) {
        return;
      }
      openModal({
        content: PopupType.PAGE_SETTING_IN_PORTAL,
        showCloseButton: false,
        payload: {
          payloadType: PopupType.PAGE_SETTING_IN_PORTAL,
          workspaceId,
          globalId: noteId,
        },
        position: getContextMenuPositionByTarget(portalButtonRef.current),
        additionalLayoutStyles: 'bg-transparent',
      });
    },
    [openModal, noteId, workspaceId]
  );

  const handleAddToPortalClick = useCallback(async () => {
    if (!portalsUsage) {
      console.warn('Empty portalsUsage');
      return;
    }
    if (!workspaceId) {
      console.warn('Empty workspaceId');
      return;
    }

    let workspacePortal = null;
    if (orgPortals && orgPortals.length) {
      workspacePortal = orgPortals.find((portal) => portal.workspaceId === workspaceId);
    }

    if (workspacePortal) {
      if (!portalButtonRef.current) {
        return;
      }
      openModal({
        content: PopupType.ADD_PAGE_TO_PORTAL,
        showCloseButton: false,
        payload: {
          payloadType: PopupType.ADD_PAGE_TO_PORTAL,
          workspaceId,
          globalId: noteId,
        },
        position: getContextMenuPositionByTarget(portalButtonRef.current),
        additionalLayoutStyles: 'bg-transparent',
      });
      return;
    }

    createPortal({
      router: { routerPushFn: router.push, routerType: NextRouterType.APP },
      organization: currentOrganization,
      workspaceId,
      portalsUsage,
      orgPortals,
      isFreePlan,
      isBusinessTrialActive,
      opCanCreatePortal,
      opCanManageBilling,
      openModal,
      closeModal,
      getOpenPaywallPopup,
      openPortalAddonPupup,
    });
  }, [
    portalsUsage,
    workspaceId,
    orgPortals,
    router,
    currentOrganization,
    isFreePlan,
    isBusinessTrialActive,
    opCanCreatePortal,
    openModal,
    closeModal,
    noteId,
    getOpenPaywallPopup,
    openPortalAddonPupup,
    opCanManageBilling,
  ]);

  let portalButton;
  const portalButtonRef = useRef<HTMLButtonElement | null>(null);
  if (note && (note.isSharedForPortal || portalPageUrl)) {
    const icon =
      modalStore.length &&
      modalStore[modalStore.length - 1].isShown &&
      modalStore[modalStore.length - 1].content === PopupType.PAGE_SETTING_IN_PORTAL
        ? IconsMap.ARROW_UP
        : IconsMap.ARROW_DOWN;
    // button that activates the note's settings in portal popup

    portalButton = (
      <Button
        data-cy="topbar-addedToPortal-button"
        type={ButtonType.STROKED}
        size={ButtonSize.XL2}
        ref={portalButtonRef}
        onClick={handleAddedToPortalClick}
      >
        <Icon icon={noteInMenu?.visible ? IconsMap.EYE_OPEN : IconsMap.EYE_CLOSE} className="w-[20px] h-[20px]" />
        <span className="ml-2 mr-2">Added to portal</span>
        <Icon icon={icon} className="w-[20px] h-[20px]" />
      </Button>
    );
  } else {
    // button activates the note's adding to portal
    portalButton = (
      <Button
        data-cy="topbar-addToPortal-button"
        onClick={handleAddToPortalClick}
        ref={portalButtonRef}
        type={ButtonType.FLAT}
        size={ButtonSize.XL2}
      >
        <Icon icon={IconsMap.PORTAL_8} className="w-[20px] h-[20px] text-white" />
        <span className="ml-2">{t('webnotes-add_to_portal')}</span>
      </Button>
    );
  }

  const handleShareClick = useCallback(() => {
    openModal({
      content: PopupType.SHARE,
      showCloseButton: false,
      payload: {
        payloadType: PopupType.SHARE,
        globalId: noteId,
      },
      usePadding: false,
    });
  }, [noteId, openModal]);

  const setNoteFullScreen = useEvent(setNoteFullScreenEvent);
  const handleNoteFullScreenClick = useCallback(() => {
    setNoteFullScreen(!isNoteFullScreen);
  }, [setNoteFullScreen, isNoteFullScreen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNoteFullScreen(false);
      }
    };

    if (isNoteFullScreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    // Return the cleanup function to remove the event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isNoteFullScreen, setNoteFullScreen]);

  const handleAskAiClick = useCallback(() => {
    webEditor?.plugins?.aiManager?.promptForPage();
  }, [webEditor]);

  return (
    <div className="flex ">
      {/*<NoteBreadcrumbs noteMenuItem={noteInMenu} />*/}
      <div className="flex items-center gap-4">
        {portalSettings.id && portalSettings.isCustomizerAccessDenied ? null : portalButton}
        <Button
          size={ButtonSize.XL2}
          type={note?.shared ? ButtonType.STROKED : ButtonType.BASIC}
          data-cy="topbar-share-button"
          onClick={handleShareClick}
        >
          <Icon icon={IconsMap.SHARE} className="w-[20px] h-[20px]" />
          <span className="ml-2">{note?.shared ? t('webnotes-shared') : t('webnotes-share')}</span>
        </Button>
        <Button type={ButtonType.BASIC} size={ButtonSize.XL2} data-cy="topbar-askai-button" onClick={handleAskAiClick}>
          <Icon icon={IconsMap.MAGIC_1} className="w-[20px] h-[20px]" />
          <span className="ml-2">{t('webnotes-ask_ai')}</span>
        </Button>
        <hr className="w-[1px] h-[32px] bg-neutral-200" />
        <div className="flex gap-4 items-center py-2">
          <div onClick={handleNoteFullScreenClick}>
            {isNoteFullScreen ? (
              <Icon
                icon={IconsMap.FULLSCREEN_IN_1}
                className="w-[20px] h-[20px] text-neutral-500 hover:bg-zinc-50 rounded cursor-pointer"
              />
            ) : (
              <Icon
                icon={IconsMap.FULLSCREEN_IN}
                className="w-[20px] h-[20px] text-neutral-500 hover:bg-zinc-50 rounded cursor-pointer"
              />
            )}
          </div>
        </div>
        <PageMenu />
      </div>
    </div>
  );
}
